import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Tooltip,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { indicatorsAPI } from '../services/api';
import { PillarSkeleton } from '../components/pillar';

function Pillar() {
  const { t } = useTranslation();
  const [pillars, setPillars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editDialog, setEditDialog] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [selectedPillar, setSelectedPillar] = useState(null);
  const [formData, setFormData] = useState({ pillar_name: '', pillar_weight: 0 });

  useEffect(() => {
    fetchPillars();
  }, []);

  const fetchPillars = async () => {
    try {
      setLoading(true);
      const response = await indicatorsAPI.getPillarWeights();
      setPillars(response.data.sort((a, b) => a.pillar_code.localeCompare(b.pillar_code)));
      setLoading(false);
    } catch (error) {
      console.error('Error fetching pillars:', error);
      setLoading(false);
    }
  };

  const handleEdit = (pillar) => {
    setSelectedPillar(pillar);
    setFormData({
      pillar_name: pillar.pillar_name,
      pillar_weight: pillar.pillar_weight * 100,
    });
    setEditDialog(true);
  };

  const handleDelete = (pillar) => {
    setSelectedPillar(pillar);
    setDeleteDialog(true);
  };

  const handleSave = async () => {
    try {
      await indicatorsAPI.updatePillarWeight(selectedPillar.pillar_code, {
        pillar_name: formData.pillar_name,
        pillar_weight: formData.pillar_weight / 100,
      });
      setEditDialog(false);
      fetchPillars();
    } catch (error) {
      console.error('Error updating pillar:', error);
    }
  };

  const handleConfirmDelete = async () => {
    try {
      await indicatorsAPI.deletePillarWeight(selectedPillar.pillar_code);
      setDeleteDialog(false);
      fetchPillars();
    } catch (error) {
      console.error('Error deleting pillar:', error);
    }
  };

  const weightColor = (w) => {
    if (w >= 0.25) return '#059669';
    if (w >= 0.20) return '#3b82f6';
    if (w >= 0.15) return '#d97706';
    return '#dc2626';
  };

  if (loading) {
    return <PillarSkeleton />;
  }

  return (
    <Box sx={{ px: 0, py: { xs: 1, sm: 2 } }}>
      {/* Header */}
      <Box sx={{ mb: 2 }}>
        <Typography variant="h5" sx={{ fontWeight: 700, lineHeight: 1.3 }}>
          {t('pillar.title', 'Pillar Management')}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
          {t('pillar.subtitle', 'Manage and configure pillar weights for indicator scoring')}
        </Typography>
      </Box>

      {/* Pillars Table */}
      <Box sx={{
        bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider',
        borderRadius: 2.5, overflow: 'hidden',
      }}>
        <Box sx={{ px: 2.5, py: 1.5, borderBottom: '1px solid', borderColor: 'divider', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="subtitle2" fontWeight={700} sx={{ fontSize: '0.85rem' }}>
            {t('pillar.title', 'Pillars')}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {pillars.length} pillars
          </Typography>
        </Box>

        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                {[
                  { label: 'Code', minW: 90 },
                  { label: 'Name', minW: 140 },
                  { label: 'Weight', minW: 100 },
                  { label: '', minW: 60, align: 'center' },
                ].map((col, i) => (
                  <TableCell
                    key={i}
                    align={col.align || 'left'}
                    sx={{
                      fontWeight: 700, fontSize: '0.72rem', py: 1, px: 1.25,
                      minWidth: col.minW, whiteSpace: 'nowrap', bgcolor: 'background.paper',
                      borderBottom: '2px solid', borderColor: 'divider',
                      textTransform: 'uppercase', letterSpacing: 0.3, color: 'text.secondary',
                    }}
                  >
                    {col.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {pillars.map((pillar) => {
                const wc = weightColor(pillar.pillar_weight);
                return (
                  <TableRow key={pillar.pillar_code} hover sx={{ '&:last-child td': { borderBottom: 0 } }}>
                    <TableCell sx={{ py: 1.25, px: 1.25 }}>
                      <Typography variant="body2" sx={{ fontWeight: 700, fontSize: '0.8rem', color: 'primary.main' }}>
                        {pillar.pillar_code}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ py: 1.25, px: 1.25 }}>
                      <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                        {pillar.pillar_name}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ py: 1.25, px: 1.25 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                        <Typography variant="body2" sx={{ fontWeight: 700, fontSize: '0.82rem', color: wc, fontFeatureSettings: '"tnum"' }}>
                          {(pillar.pillar_weight * 100).toFixed(2)}%
                        </Typography>
                        <Box sx={{ width: 40, height: 4, borderRadius: 2, bgcolor: `${wc}18` }}>
                          <Box sx={{ width: `${Math.min(pillar.pillar_weight * 100 * 4, 100)}%`, height: '100%', bgcolor: wc, borderRadius: 2 }} />
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell align="center" sx={{ py: 1.25, px: 0.5 }}>
                      <Tooltip title="Edit" arrow>
                        <IconButton size="small" onClick={() => handleEdit(pillar)} sx={{ color: 'text.secondary', '&:hover': { color: 'primary.main' } }}>
                          <EditIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete" arrow>
                        <IconButton size="small" onClick={() => handleDelete(pillar)} sx={{ color: 'text.disabled', ml: 0.5, '&:hover': { color: 'error.main' } }}>
                          <DeleteIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      {/* Edit Dialog */}
      <Dialog 
        open={editDialog} 
        onClose={() => setEditDialog(false)} 
        maxWidth="sm" 
        fullWidth
        PaperProps={{ sx: { borderRadius: 2.5 } }}
      >
        <DialogTitle sx={{ fontWeight: 700, fontSize: '1rem', pb: 1 }}>
          Edit Pillar: {selectedPillar?.pillar_code}
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <TextField
            label="Pillar Name"
            fullWidth
            margin="normal"
            value={formData.pillar_name}
            onChange={(e) => setFormData({ ...formData, pillar_name: e.target.value })}
            sx={{ mb: 2 }}
          />
          <TextField
            label="Pillar Weight (%)"
            fullWidth
            margin="normal"
            type="number"
            value={formData.pillar_weight}
            onChange={(e) => setFormData({ ...formData, pillar_weight: parseFloat(e.target.value) })}
            slotProps={{ htmlInput: { min: 0, max: 100, step: 0.01 } }}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2.5 }}>
          <Button onClick={() => setEditDialog(false)} sx={{ fontWeight: 600 }}>
            Cancel
          </Button>
          <Button 
            onClick={handleSave} 
            variant="contained" 
            sx={{ fontWeight: 600, px: 3 }}
          >
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog 
        open={deleteDialog} 
        onClose={() => setDeleteDialog(false)}
        PaperProps={{ sx: { borderRadius: 2.5 } }}
      >
        <DialogTitle sx={{ fontWeight: 700, fontSize: '1rem' }}>
          Confirm Delete
        </DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete pillar <strong>{selectedPillar?.pillar_code}</strong>?
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2.5 }}>
          <Button onClick={() => setDeleteDialog(false)} sx={{ fontWeight: 600 }}>
            Cancel
          </Button>
          <Button 
            onClick={handleConfirmDelete} 
            variant="contained" 
            color="error"
            sx={{ fontWeight: 600, px: 3 }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default Pillar;
